import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Avatar, CreatorName, formatNum, Loading, Empty, SectionHead, SimBadge } from "../components/common";
import { usePlayer } from "../context/PlayerContext";
import { Button } from "../components/ui/button";
import { Search, Play, UserPlus, Check, Plus, Trophy, ChevronRight, Radio, BarChart3, Users as UsersRound, Handshake, Instagram, Share2, Store } from "lucide-react";
import { toast } from "sonner";
import { StoryShareModal } from "../components/StoryShareModal";
import { ShareToVSModal } from "../components/ShareToVSModal";

export default function Explore() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const [query, setQuery] = useState(q);
  const [results, setResults] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const player = usePlayer();

  useEffect(() => {
    api.get("/suggested").then(({ data }) => setSuggested(data)).catch(() => {});
    api.get("/music/tracks?sort=trending").then(({ data }) => setTracks(data.slice(0, 8))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q) { setResults(null); return; }
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).finally(() => setLoading(false));
  }, [q]);

  const follow = async (u, i) => {
    try { await api.post(`/follow/${u.id}`); setSuggested((s) => s.map((x, j) => j === i ? { ...x, _followed: true } : x)); toast.success(`Following ${u.display_name}`); }
    catch { toast.error("Could not follow"); }
  };

  return (
    <div className="px-4 md:px-6 py-5">
      <form onSubmit={(e) => { e.preventDefault(); setSp(query ? { q: query } : {}); }} className="flex items-center gap-2 surface rounded-full px-4 h-12 mb-6 max-w-2xl">
        <Search size={18} className="text-muted-foreground" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search creators, music, shows, events, hashtags…" className="bg-transparent flex-1 outline-none" data-testid="explore-search-input" />
      </form>

      {/* Mobile quick-access (desktop uses the sidebar) */}
      <div className="md:hidden mb-6" data-testid="explore-mobile-shortcuts">
        <Link to="/awards" data-testid="explore-vawards" className="relative overflow-hidden flex items-center gap-3 rounded-2xl p-4 mb-3 border border-gold/25 bg-gradient-to-r from-gold/15 to-transparent">
          <div className="w-11 h-11 rounded-xl bg-gold/15 flex items-center justify-center text-gold shrink-0"><Trophy size={22} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base leading-tight">VirtuAwards</div>
            <div className="text-xs text-muted-foreground">Vote for the metaverse's best creators</div>
          </div>
          <ChevronRight size={18} className="text-gold shrink-0" />
        </Link>
        <div className="flex gap-2 overflow-x-auto hide-scroll">
          {[["/live", Radio, "Live"], ["/charts", BarChart3, "Charts"], ["/crews", UsersRound, "VCrews"], ["/hire", Handshake, "Hire"], ["/businesses", Store, "Business"]].map(([to, Icon, label]) => (
            <Link key={to} to={to} className="shrink-0 flex items-center gap-1.5 surface-2 rounded-full px-3.5 h-9 text-sm text-muted-foreground"><Icon size={15} />{label}</Link>
          ))}
        </div>
      </div>

      {q ? (
        loading ? <Loading /> : results && (
          <div className="space-y-8">
            {results.users?.length > 0 && <div><SectionHead title="Creators" /><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{results.users.map((u) => <CreatorCard key={u.id} u={u} />)}</div></div>}
            {results.tracks?.length > 0 && <div><SectionHead title="Music" /><div className="space-y-2">{results.tracks.map((t) => <TrackRow key={t.id} t={t} onPlay={() => player.playTrack(t, results.tracks)} />)}</div></div>}
            {results.videos?.length > 0 && <div><SectionHead title="VStar TV" /><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{results.videos.map((v) => <Link key={v.id} to={`/video/${v.id}`} className="surface rounded-xl overflow-hidden card-hover"><img src={v.thumbnail_url} className="w-full aspect-video object-cover" alt="" /><div className="p-3 text-sm font-medium">{v.title}</div></Link>)}</div></div>}
            {!results.users?.length && !results.tracks?.length && !results.videos?.length && <Empty icon={Search} title="No results" hint={`Nothing found for "${q}"`} />}
          </div>
        )
      ) : (
        <div className="space-y-10">
          <div>
            <SectionHead title="Creators to discover" sub="Fresh talent across the VirtuStars industry" />
            {suggested.length === 0 ? <Loading /> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {suggested.map((u, i) => <CreatorCard key={u.id} u={u} followed={u._followed} onFollow={() => follow(u, i)} />)}
            </div>}
          </div>
          <div>
            <SectionHead title="Trending on VStar Music" action={<Link to="/music" className="text-sm text-eblue">See all →</Link>} />
            <div className="space-y-2">{tracks.map((t) => <TrackRow key={t.id} t={t} onPlay={() => player.playTrack(t, tracks)} />)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CreatorCard({ u, followed, onFollow }) {
  return (
    <div className="surface rounded-2xl p-4 card-hover">
      <div className="flex items-center gap-3">
        <Link to={`/${u.username}`}><Avatar src={u.avatar_url} name={u.display_name} size={52} /></Link>
        <div className="min-w-0 flex-1">
          <Link to={`/${u.username}`} className="font-semibold hover:text-eblue block truncate"><CreatorName user={u} /></Link>
          <div className="text-xs text-muted-foreground truncate">{u.roles?.primary} · {formatNum(u.follower_count)} followers</div>
        </div>
      </div>
      {onFollow && <Button onClick={onFollow} disabled={followed} className={`w-full mt-3 rounded-full h-9 ${followed ? "btn-gold text-black disabled:opacity-100" : "btn-blue"}`} data-testid={`follow-${u.username}`}>
        {followed ? <><Check size={15} className="mr-1" /> Following</> : <><UserPlus size={15} className="mr-1" /> Follow</>}
      </Button>}
    </div>
  );
}

export function TrackRow({ t, onPlay, rank, onAdd }) {
  const [story, setStory] = useState(false);
  const [vs, setVs] = useState(false);
  return (
    <div className="flex items-center gap-3 surface rounded-xl p-2.5 pr-4 card-hover group">
      {rank != null && <span className="font-mono text-gold w-6 text-center">{rank}</span>}
      <button onClick={onPlay} className="relative shrink-0" data-testid={`play-track-${t.id}`}>
        <img src={t.cover_url} className="w-12 h-12 rounded-lg object-cover" alt="" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"><Play size={18} className="text-white fill-white" /></span>
      </button>
      <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate flex items-center gap-1.5">{t.title}{t.is_demo && <SimBadge />}</div><Link to={t.artist ? `/${t.artist.username}` : "#"} className="text-xs text-muted-foreground hover:text-eblue truncate block">{t.artist?.display_name}</Link></div>
      <span className="text-xs text-muted-foreground hidden sm:block">{t.genre}</span>
      <span className="text-xs font-mono text-silver">{formatNum(t.stream_count)}</span>
      {t.artist?.username && <button onClick={() => setVs(true)} data-testid={`share-track-${t.id}`} className="text-muted-foreground hover:text-eblue p-1" title="Share to VirtuStars feed or story"><Share2 size={16} /></button>}
      {t.artist?.username && <button onClick={() => setStory(true)} data-testid={`story-track-${t.id}`} className="text-muted-foreground hover:text-eblue p-1" title="Share to Instagram story"><Instagram size={16} /></button>}
      {onAdd && <button onClick={() => onAdd(t)} data-testid={`add-to-playlist-${t.id}`} className="text-muted-foreground hover:text-eblue p-1" title="Add to playlist"><Plus size={16} /></button>}
      {story && <StoryShareModal username={t.artist.username} options={[{ type: "track", label: "This Track", title: t.title, cover: t.cover_url }, { type: "profile", label: "Profile" }]} onClose={() => setStory(false)} />}
      {vs && <ShareToVSModal entity={{ kind: "track", id: t.id, title: t.title, cover: t.cover_url, author: t.artist }} onClose={() => setVs(false)} />}
    </div>
  );
}
